import { lazy, Suspense, useCallback, useMemo, useRef, useState } from 'react'
import { Box, Check, Download, FileImage, FolderPlus, Grid3X3, Keyboard, Maximize2, Redo2, Rotate3D, Save, Undo2, Upload } from 'lucide-react'
import LeftSidebar from './components/LeftSidebar'
import RightSidebar from './components/RightSidebar'
import CustomElementModal from './components/CustomElementModal'
import { calculateTotal, constrainElement, createElement, DEFAULT_PROJECT, definitionFromItem, ELEMENT_SCHEMA, formatPrice, getElementLabel, getElementPrice, normalizeDefinition, normalizeProject, PROJECT_SCHEMA, structuralWallCount } from './data'
import { fileToGraphic } from './graphics'

const BoothCanvas = lazy(() => import('./components/BoothCanvas'))
const STORAGE_KEY = 'iqexpo-studio-project-v1'

function loadProject() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    return saved ? normalizeProject(JSON.parse(saved)) : structuredClone(DEFAULT_PROJECT)
  } catch {
    return structuredClone(DEFAULT_PROJECT)
  }
}

function exportFile(blob, filename) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

function fileSlug(value) {
  return String(value || 'iqexpo').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

async function readJsonFile(file) {
  if (!file) throw new Error('Nie wybrano pliku.')
  return JSON.parse(await file.text())
}

export default function App() {
  const [project, setProject] = useState(loadProject)
  const [selectedId, setSelectedId] = useState(project.elements[0]?.id ?? null)
  const [view, setView] = useState('perspective')
  const [saved, setSaved] = useState(true)
  const [history, setHistory] = useState([])
  const [future, setFuture] = useState([])
  const [notice, setNotice] = useState('')
  const [creatorOpen, setCreatorOpen] = useState(false)
  const canvasApi = useRef(null)
  const noticeTimer = useRef(null)

  const selected = useMemo(() => project.elements.find((item) => item.id === selectedId) ?? null, [project.elements, selectedId])
  const total = useMemo(() => calculateTotal(project), [project])
  const area = project.dimensions.width * project.dimensions.depth
  const elementCount = project.elements.length + structuralWallCount(project.topology)

  const showNotice = useCallback((message) => {
    setNotice(message)
    window.clearTimeout(noticeTimer.current)
    noticeTimer.current = window.setTimeout(() => setNotice(''), 2800)
  }, [])

  const commit = useCallback((updater) => {
    setProject((current) => {
      const next = typeof updater === 'function' ? updater(current) : updater
      setHistory((items) => [...items.slice(-24), current])
      setFuture([])
      setSaved(false)
      return next
    })
  }, [])

  const setTopology = useCallback((topology) => commit((current) => ({ ...current, topology })), [commit])
  const setDimension = useCallback((key, value) => {
    if (!Number.isFinite(value)) return
    commit((current) => {
      const dimensions = { ...current.dimensions, [key]: value }
      return { ...current, dimensions, elements: current.elements.map((item) => constrainElement(item, dimensions)) }
    })
  }, [commit])

  const addElement = useCallback((type) => {
    const newItem = createElement(type, project.elements.length, project.dimensions)
    commit((current) => ({ ...current, elements: [...current.elements, newItem] }))
    setSelectedId(newItem.id)
    showNotice(`${getElementLabel(newItem)} dodano do projektu`)
  }, [commit, project.dimensions, project.elements.length, showNotice])

  const addCustomElement = useCallback((definition) => {
    const newItem = createElement('custom', project.elements.length, project.dimensions, definition)
    commit((current) => ({ ...current, elements: [...current.elements, newItem] }))
    setSelectedId(newItem.id)
    showNotice(`${definition.name} dodano do projektu`)
  }, [commit, project.dimensions, project.elements.length, showNotice])

  const createCustomElement = useCallback((values) => {
    const definition = normalizeDefinition(values)
    const newItem = createElement('custom', project.elements.length, project.dimensions, definition)
    commit((current) => ({ ...current, customElements: [...current.customElements, definition], elements: [...current.elements, newItem] }))
    setSelectedId(newItem.id)
    setCreatorOpen(false)
    showNotice(`${definition.name} utworzono i dodano do biblioteki`)
  }, [commit, project.dimensions, project.elements.length, showNotice])

  const updateSelected = useCallback((changes) => {
    if (!selectedId) return
    commit((current) => ({ ...current, elements: current.elements.map((item) => item.id === selectedId ? constrainElement({ ...item, ...changes }, current.dimensions) : item) }))
  }, [commit, selectedId])

  const uploadWallGraphic = useCallback(async (surface, event) => {
    try {
      showNotice('Przetwarzanie grafiki…')
      const graphic = await fileToGraphic(event.target.files?.[0])
      commit((current) => ({ ...current, surfaceGraphics: { ...current.surfaceGraphics, [surface]: graphic } }))
      showNotice(`Grafikę „${graphic.name}” dodano do ściany`)
    } catch (error) {
      showNotice(error instanceof Error ? error.message : 'Nie udało się dodać grafiki')
    } finally {
      event.target.value = ''
    }
  }, [commit, showNotice])

  const removeWallGraphic = useCallback((surface) => {
    commit((current) => {
      const surfaceGraphics = { ...current.surfaceGraphics }
      delete surfaceGraphics[surface]
      return { ...current, surfaceGraphics }
    })
    showNotice('Grafikę usunięto ze ściany')
  }, [commit, showNotice])

  const uploadSelectedGraphic = useCallback(async (face, event) => {
    if (!selectedId) return
    try {
      showNotice('Przetwarzanie grafiki…')
      const graphic = await fileToGraphic(event.target.files?.[0])
      commit((current) => ({
        ...current,
        elements: current.elements.map((item) => item.id === selectedId ? { ...item, graphics: { ...item.graphics, [face]: graphic } } : item),
      }))
      showNotice(`Grafikę „${graphic.name}” przypisano do powierzchni`)
    } catch (error) {
      showNotice(error instanceof Error ? error.message : 'Nie udało się dodać grafiki')
    } finally {
      event.target.value = ''
    }
  }, [commit, selectedId, showNotice])

  const removeSelectedGraphic = useCallback((face) => {
    if (!selectedId) return
    commit((current) => ({
      ...current,
      elements: current.elements.map((item) => {
        if (item.id !== selectedId) return item
        const graphics = { ...item.graphics }
        delete graphics[face]
        return { ...item, graphics }
      }),
    }))
    showNotice('Grafikę usunięto z powierzchni')
  }, [commit, selectedId, showNotice])

  const transformElement = useCallback((id, changes) => {
    commit((current) => ({ ...current, elements: current.elements.map((item) => item.id === id ? constrainElement({ ...item, ...changes }, current.dimensions) : item) }))
  }, [commit])

  const duplicateSelected = useCallback(() => {
    if (!selected) return
    const duplicate = constrainElement({ ...selected, id: `${selected.type}-${Date.now()}`, x: selected.x + 0.5, z: selected.z + 0.5 }, project.dimensions)
    commit((current) => ({ ...current, elements: [...current.elements, duplicate] }))
    setSelectedId(duplicate.id)
    showNotice('Element został zduplikowany')
  }, [commit, project.dimensions, selected, showNotice])

  const deleteSelected = useCallback(() => {
    if (!selectedId) return
    commit((current) => ({ ...current, elements: current.elements.filter((item) => item.id !== selectedId) }))
    setSelectedId(null)
    showNotice('Element usunięto z projektu')
  }, [commit, selectedId, showNotice])

  const saveProject = useCallback(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(project))
      setSaved(true)
      showNotice('Projekt zapisano w przeglądarce')
    } catch {
      showNotice('Projekt z grafikami jest zbyt duży dla pamięci przeglądarki — wyeksportuj JSON')
    }
  }, [project, showNotice])

  const newProject = useCallback(() => {
    const fresh = structuredClone(DEFAULT_PROJECT)
    commit(fresh)
    setSelectedId(fresh.elements[0]?.id ?? null)
    showNotice('Utworzono nowy projekt')
  }, [commit, showNotice])

  const undo = useCallback(() => {
    if (!history.length) return
    const previous = history.at(-1)
    setHistory((items) => items.slice(0, -1)); setFuture((items) => [project, ...items].slice(0, 25)); setProject(previous); setSaved(false)
  }, [history, project])

  const redo = useCallback(() => {
    if (!future.length) return
    const next = future[0]
    setFuture((items) => items.slice(1)); setHistory((items) => [...items, project].slice(-25)); setProject(next); setSaved(false)
  }, [future, project])

  const exportProject = useCallback(() => {
    const payload = { schema: PROJECT_SCHEMA, version: 2, exportedAt: new Date().toISOString(), project }
    exportFile(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }), `${fileSlug(project.name)}.iqexpo-project.json`)
    showNotice('Konfigurację projektu wyeksportowano')
  }, [project, showNotice])

  const importProject = useCallback(async (event) => {
    try {
      const data = await readJsonFile(event.target.files?.[0])
      const imported = normalizeProject(data.schema === PROJECT_SCHEMA ? data.project : data)
      commit(imported); setSelectedId(imported.elements[0]?.id ?? null); setView('perspective')
      showNotice('Projekt został poprawnie zaimportowany')
    } catch (error) {
      showNotice(error instanceof Error ? error.message : 'Nie udało się zaimportować projektu')
    } finally { event.target.value = '' }
  }, [commit, showNotice])

  const importElement = useCallback(async (event) => {
    try {
      const data = await readJsonFile(event.target.files?.[0])
      const definition = normalizeDefinition(data.schema === ELEMENT_SCHEMA ? data.definition : data.definition ?? data)
      const unique = { ...definition, id: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 6)}` }
      const newItem = createElement('custom', project.elements.length, project.dimensions, unique)
      commit((current) => ({ ...current, customElements: [...current.customElements, unique], elements: [...current.elements, newItem] }))
      setSelectedId(newItem.id); showNotice(`${unique.name} zaimportowano do biblioteki`)
    } catch (error) {
      showNotice(error instanceof Error ? error.message : 'Nie udało się zaimportować elementu')
    } finally { event.target.value = '' }
  }, [commit, project.dimensions, project.elements.length, showNotice])

  const exportElement = useCallback(() => {
    if (!selected) return
    const definition = definitionFromItem(selected)
    const payload = { schema: ELEMENT_SCHEMA, version: 1, exportedAt: new Date().toISOString(), definition }
    exportFile(new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' }), `${fileSlug(definition.name)}.iqexpo-element.json`)
    showNotice('Element wyeksportowano jako osobny plik')
  }, [selected, showNotice])

  const exportPng = useCallback(() => {
    const api = canvasApi.current
    if (!api) return
    api.gl.render(api.scene, api.camera)
    api.gl.domElement.toBlob((blob) => { if (blob) exportFile(blob, `${fileSlug(project.name)}-wizualizacja.png`) }, 'image/png')
    showNotice('Wizualizacja PNG jest gotowa')
  }, [project.name, showNotice])

  const exportPdf = useCallback(async () => {
    const { jsPDF } = await import('jspdf')
    const pdf = new jsPDF({ unit: 'mm', format: 'a4' })
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(22); pdf.text('IQEXPO Studio', 18, 22); pdf.setFontSize(15); pdf.text(project.name, 18, 34)
    pdf.setDrawColor(37, 99, 235); pdf.setLineWidth(1.2); pdf.line(18, 40, 192, 40); pdf.setFont('helvetica', 'normal'); pdf.setFontSize(10)
    pdf.text(`Uklad: ${project.topology}`, 18, 51); pdf.text(`Wymiary: ${project.dimensions.width.toFixed(2)} x ${project.dimensions.depth.toFixed(2)} x ${project.dimensions.height.toFixed(2)} m`, 18, 58); pdf.text(`Powierzchnia: ${area.toFixed(2)} m2`, 18, 65)
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(13); pdf.text('Elementy projektu', 18, 80)
    let y = 90
    project.elements.forEach((item) => { pdf.setFont('helvetica', 'normal'); pdf.setFontSize(9); pdf.text(getElementLabel(item), 20, y); pdf.text(`${formatPrice(getElementPrice(item))} zl`, 165, y, { align: 'right' }); y += 6 })
    pdf.setDrawColor(218, 226, 236); pdf.line(18, y + 2, 192, y + 2); pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14); pdf.text(`Wartosc netto: ${formatPrice(total)} zl`, 18, y + 14)
    const canvas = canvasApi.current?.gl.domElement
    if (canvas && y < 155) pdf.addImage(canvas.toDataURL('image/jpeg', 0.84), 'JPEG', 18, y + 24, 174, 104, undefined, 'FAST')
    pdf.save(`${fileSlug(project.name)}-specyfikacja.pdf`); showNotice('Specyfikacja PDF jest gotowa')
  }, [area, project, showNotice, total])

  return (
    <div className="app-shell">
      <header className="topbar">
        <div className="brand"><span>IQEXPO</span><small>Studio</small></div>
        <div className="project-name-wrap"><input aria-label="Nazwa projektu" value={project.name} onChange={(event) => commit((current) => ({ ...current, name: event.target.value }))} /><span className={`save-status ${saved ? 'saved' : ''}`}>{saved ? <Check size={13} /> : null}{saved ? 'Zapisano' : 'Niezapisane zmiany'}</span></div>
        <div className="header-actions">
          <button type="button" onClick={undo} disabled={!history.length} title="Cofnij"><Undo2 size={18} /></button><button type="button" onClick={redo} disabled={!future.length} title="Ponów"><Redo2 size={18} /></button><span className="action-divider" />
          <button type="button" onClick={newProject}><FolderPlus size={18} /><span>Nowy projekt</span></button><button type="button" onClick={saveProject}><Save size={18} /><span>Zapisz</span></button>
          <label className="header-file-action data-action"><Upload size={17} /><span>Import JSON</span><input type="file" accept=".json,.iqexpo-project.json" onChange={importProject} /></label>
          <button className="data-action" type="button" onClick={exportProject}><Download size={17} /><span>Projekt JSON</span></button>
          <button className="primary" type="button" onClick={exportPng}><Download size={18} /><span>Eksportuj PNG</span></button>
        </div>
      </header>

      <main className="workspace">
        <LeftSidebar project={project} setTopology={setTopology} setDimension={setDimension} addElement={addElement} addCustomElement={addCustomElement} onOpenCreator={() => setCreatorOpen(true)} onImportElement={importElement} onUploadWallGraphic={uploadWallGraphic} onRemoveWallGraphic={removeWallGraphic} />
        <section className="canvas-panel">
          <div className="view-switcher" role="group" aria-label="Widok kamery">{[['perspective', 'Perspektywa'], ['front', 'Przód'], ['top', 'Góra']].map(([id, label]) => <button type="button" key={id} className={view === id ? 'active' : ''} onClick={() => setView(id)}>{id === 'perspective' ? <Box size={16} /> : id === 'front' ? <FileImage size={16} /> : <Grid3X3 size={16} />}{label}</button>)}</div>
          <Suspense fallback={<div className="canvas-loading"><Box size={28} /><span>Ładowanie sceny 3D…</span></div>}><BoothCanvas project={project} selectedId={selectedId} onSelect={setSelectedId} onTransform={transformElement} view={view} onCanvasReady={(api) => { canvasApi.current = api }} /></Suspense>
          <div className="canvas-help"><Rotate3D size={17} /><span>Obiekt — przesuń · tło — obrót</span><span className="help-divider" /><Maximize2 size={17} /><span>Scroll, aby przybliżyć</span></div>
        </section>
        <RightSidebar selected={selected} elements={project.elements} topology={project.topology} total={total} onUpdate={updateSelected} onDuplicate={duplicateSelected} onDelete={deleteSelected} onSelect={setSelectedId} onExportPdf={exportPdf} onExportElement={exportElement} onUploadGraphic={uploadSelectedGraphic} onRemoveGraphic={removeSelectedGraphic} />
      </main>

      <footer className="statusbar"><span><Grid3X3 size={17} /><strong>Powierzchnia</strong> {area.toFixed(2)} m²</span><span><Box size={17} /><strong>{elementCount}</strong> elementów</span><span><Grid3X3 size={17} />Siatka 0,25 m</span><span className="shortcut"><Keyboard size={17} />Granice stoiska i kolizje ścian aktywne</span><span className="status-price">Wartość netto <strong>{formatPrice(total)} zł</strong></span></footer>
      {notice ? <div className="toast" role="status"><Check size={17} />{notice}</div> : null}
      {creatorOpen ? <CustomElementModal boothHeight={project.dimensions.height} onClose={() => setCreatorOpen(false)} onCreate={createCustomElement} /> : null}
    </div>
  )
}
